import { useState } from "react";
import { UserPlus } from "lucide-react";
import {
  Form,
  required,
  useCreate,
  useGetIdentity,
  useGetList,
  useNotify,
  useTranslate,
} from "ra-core";
import type { Identifier } from "ra-core";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { contactOptionText } from "../misc/ContactOption";
import type { OutreachList, OutreachListMember } from "../types";

export const AddContactToListDialog = ({
  list,
}: {
  list: OutreachList;
}) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        <UserPlus className="h-4 w-4" />
        {translate("resources.outreach_lists.action.add_contact", {
          _: "Add contact",
        })}
      </Button>
      {open && (
        <AddContactToListDialogContent
          list={list}
          open={open}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

const AddContactToListDialogContent = ({
  list,
  open,
  onClose,
}: {
  list: OutreachList;
  open: boolean;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [contactId, setContactId] = useState<Identifier | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [create] = useCreate();

  // list.criteria.list_kind ('offer'|'channel') decides which axis this
  // dialog writes to — see backlog.md's "Design pattern — Offer vs. Channel
  // are two independent axes" (2026-09-08). A contact can be active in one
  // list PER AXIS, not one list total.
  const listKind = list.criteria?.list_kind ?? "offer";

  // Client-side exclusivity check: does this contact already have an active
  // membership of the SAME kind (possibly a different list)? Checked before
  // we ever hit the DB's partial-unique-index rejection
  // (outreach_list_members_one_active_per_kind), so we can name the list
  // instead of surfacing a raw constraint-violation error.
  const { data: existingMemberships, isPending: isCheckingExisting } =
    useGetList<OutreachListMember>(
      "outreach_list_members",
      {
        filter: {
          contact_id: contactId,
          list_kind: listKind,
          "removed_at@is": null,
        },
        pagination: { page: 1, perPage: 1 },
      },
      { enabled: !!contactId },
    );

  const { data: allLists } = useGetList<OutreachList>("outreach_lists", {
    pagination: { page: 1, perPage: 100 },
  });

  const existingMembership = existingMemberships?.[0];
  const existingListName = existingMembership
    ? (allLists?.find((l) => l.id === existingMembership.list_id)?.name ??
      `list #${existingMembership.list_id}`)
    : null;

  const isBlocked = !!existingMembership;

  const handleSubmit = async () => {
    if (!contactId || isBlocked) return;
    setIsSubmitting(true);
    try {
      await create(
        "outreach_list_members",
        {
          data: {
            list_id: list.id,
            contact_id: contactId,
            added_by: identity?.fullName ?? "manual",
            list_kind: listKind,
          },
        },
        { returnPromise: true },
      );
      notify("resources.outreach_lists.action.add_contact_success", {
        type: "success",
        messageArgs: { _: "Contact added to the list" },
      });
      onClose();
    } catch (error: any) {
      // 23505 = unique_violation - the DB's exclusivity index rejected a
      // race we didn't catch client-side (e.g. someone else added this
      // contact elsewhere between our check and this submit). PostgREST's
      // error code lands in the HttpError body, not on the error itself.
      const isExclusivityConflict =
        error?.body?.code === "23505" ||
        String(error?.body?.message ?? error?.message ?? "")
          .toLowerCase()
          .includes("duplicate");
      notify(
        isExclusivityConflict
          ? "resources.outreach_lists.errors.already_in_list"
          : "resources.outreach_lists.errors.add_contact_failed",
        {
          type: "error",
          messageArgs: {
            _: isExclusivityConflict
              ? `This contact is already active in another ${listKind === "channel" ? "Channel" : "Offer"}`
              : "Failed to add contact to the list",
          },
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("resources.outreach_lists.action.add_contact_title", {
              _: `Add a contact to ${list.name}`,
              listName: list.name,
            })}
          </DialogTitle>
          <DialogDescription>
            {translate(
              "resources.outreach_lists.action.add_contact_description",
              {
                _:
                  listKind === "channel"
                    ? "A contact can only be active in one Channel at a time."
                    : "A contact can only be active in one Offer at a time.",
              },
            )}
          </DialogDescription>
        </DialogHeader>

        <Form>
          <ReferenceInput source="contact_id" reference="contacts_summary">
            <AutocompleteInput
              label={false}
              optionText={contactOptionText}
              helperText={false}
              validate={required()}
              onChange={(value) => setContactId(value ?? null)}
              modal
            />
          </ReferenceInput>
        </Form>

        {contactId && !isCheckingExisting && isBlocked && (
          <Alert variant="destructive">
            <AlertDescription>
              {translate("resources.outreach_lists.errors.already_in_list", {
                _: `This contact is already active in "${existingListName}". Remove them from that list first.`,
                listName: existingListName,
              })}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!contactId || isBlocked || isSubmitting || isCheckingExisting}
          >
            {isSubmitting
              ? translate("resources.outreach_lists.action.adding", {
                  _: "Adding...",
                })
              : translate("resources.outreach_lists.action.add_contact", {
                  _: "Add contact",
                })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
